import type { Translation } from '@/i18n/dictionary'
import type { WrittenPage } from '@/content/sections'

/**
 * `/tr/impact-labs/benchmark`. Kopya yalnızca; route'u
 * `content/pages/benchmark.ts` bir kez tanımlıyor.
 *
 * Sektör ve ülke adları veriden geliyor ve iki dilde de İngilizce — tool'ların
 * sektör listelerinde olduğu gibi. Çevirmek, veriyle ekranı ayrıştırırdı.
 */
export const benchmark: Translation<WrittenPage> = {
  navLabel: 'Benchmark',
  title: 'DAIMI Benchmark',
  description:
    'Sektöre, ülkeye ve şirket büyüklüğüne göre digital ve AI maturity — Digitopia’nın 1.575 şirketlik benchmark’ından. DAIMI, DMI ve AIMI yan yana, her rakamın arkasındaki şirket sayısıyla birlikte.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'BENCHMARK',
      headline: 'Sektörünüz gerçekte nerede duruyor?',
      sub: '1.575 şirket, 17 sektör, 46 ülke. Digital maturity ile AI maturity önce ayrı ayrı, sonra birlikte ölçülüyor — böylece sektörünüzü hangisinin taşıdığı, hangisinin taşımadığı görünüyor.',
      ctas: [],
    },
    {
      kind: 'benchmark',
      labels: {
        indexHeading: 'Üç indeks, sektör sektör',
        indexBlurb:
          'DMI digital maturity’yi, AIMI AI maturity’yi ölçüyor; DAIMI ikisi birden. Her dimension’daki beş section’dan biri AI olduğu için AIMI birleşik skorun %20’sini taşıyor. Asıl ilginç sayı genelde ikisinin ayrıştığı yer.',
        indexNote:
          'Digitopia benchmark’ında yayımlandığı hâliyle ortalamalar. Her satırda şirket sayısı var; beşten az şirketi olan sektör burada skorlanmıyor.',
        scopeLabel: 'Kapsam',
        scopes: { global: 'Global', turkiye: 'Türkiye', us: 'ABD', APAC: 'APAC', Americas: 'Amerika', EMEA: 'EMEA' },
        colSector: 'Sektör',
        colCompanies: 'Şirket',
        allSectors: 'Tüm sektörler',
        tooFew: 'yayımlamak için şirket sayısı yetersiz',
        pickerHeading: 'Ülkeniz, sektörünüz, büyüklüğünüz',
        pickerBlurb:
          'Tek bir (ülke, sektör, büyüklük) hücresi için altı dimension. Digitopia benchmark’ının alışılmış görünümü, güncel veri üzerine kuruldu.',
        country: 'Ülke',
        sector: 'Sektör',
        size: 'Şirket büyüklüğü',
        sizes: { S: 'Küçük', M: 'Orta', L: 'Büyük' },
        sourceLine: 'Kaynak çalışma: {source}.',
        noData: 'Benchmark’ta bu kombinasyona uyan şirket henüz yok.',
        loading: 'Benchmark yükleniyor…',
        failed: 'Benchmark verisi yüklenemedi. Sayfayı yenilemek genelde çözüyor.',

        vitalityHeading: 'The DAIMI Vitality Chart',
        vitalityBlurb:
          'Değerlendirilmiş her şirket, birbirine karşı çizilmiş iki kuvvet üzerinde — DAIMI de ikisinin ortalaması, yani bir şirketin köşegen üzerindeki mesafesi skoru demek. Velocity şirketin ne yaptığı: müşteriye nasıl dokunduğu, nasıl çalıştığı, nasıl innovation ürettiği. Endurance ise bunu sürdürmesini sağlayan taraf: insan, governance, technology. Endurance’sız hız tıkanıyor; hızsız endurance ise hiç başlamıyor.',
        vitalityNote:
          'Her nokta değerlendirilmiş bir şirket; toplam 1.575, company ID ile anonimleştirilmiş. DMI digital maturity’yi, AIMI AI maturity’yi ölçüyor; DAIMI ikisi birden — AI section her dimension’daki beş section’dan biri olduğu için birleşik skorun %20’sini taşıyor. Kesikli çizgiler global ortalamalar, Velocity 2,92 ve Endurance 2,93, ve dört profili ayırıyor; orijinal grafikte bu çizgiler yoktu.',
        allOption: 'Hepsi',
        industry: 'Ana sektör',
        axisVelocity: 'Velocity',
        axisEndurance: 'Endurance',
        figureTitle: 'Şekil: DAIMI Vitality Chart',
        companyCount: 'Şirket sayısı:',
        resetFilter: 'Filtreyi sıfırla',
        companyId: 'Company ID',
        derivedNote: 'Yukarıdaki altı dimension’dan türetildi.',
        reportEyebrow: 'Industry Focus Report',
        reportTitle: '{industry} DAIMI raporunu inceleyin',
        reportButton: 'Raporu okuyun',
        indexLabel: 'İndeks',
        indexNames: { daimi: 'DAIMI', dmi: 'DMI', aimi: 'AIMI' },
        indexAverage: 'Ekrandaki {n} şirketin {index} ortalaması.',
        aboveAverage: '{avg} olan benchmark {index} ortalamasında veya üstünde {n} şirket',
        belowAverage: 'altında {n} şirket',
        estimatedNote:
          '* AI / digital ayrışımı her ülke / sektör / büyüklük grubu için ölçülüyor ve şirketin kendi DAIMI’sine uygulanıyor — grup bazında ölçüm, şirket bazında tahmin.',
        noSplit: '{n} şirket gösterilmiyor: gruplarının kaynakta ayrışımı yok.',
        profiles: {
          ironMan: {
            name: 'Iron Man',
            blurb:
              'Sağlam zemin üzerinde hızlı gidiyor. İki skor da global ortalamanın üstünde — transformation programını bir çalışma biçimine dönüştüren kombinasyon.',
          },
          marathon: {
            name: 'Marathon Runner',
            blurb:
              'Zemin hazır — insan, governance, technology — ama üzerinde henüz pek bir şey koşmuyor. Değişme kapasitesi, değişme iştahının önünde.',
          },
          sprinter: {
            name: 'Sprinter',
            blurb:
              'Müşteri, operasyon ve innovation tarafında görünür bir momentum var; ama zemin aynı hızda büyümemiş. Tempo gerçek, yalnızca ödünç alınmış.',
          },
          walker: {
            name: 'Walker',
            blurb:
              'İkisinde de global ortalamanın altında. Ne hamle var ne de hamleyi sürdürecek kapasite — benchmark’ın büyük kısmı hâlâ burada.',
          },
        },
      },
    },
    {
      kind: 'prose',
      heading: 'Bu sayılar nasıl okunur',
      paragraphs: [
        'Her skor 1–5 ölçeğinde ve önemli olan mesafe küçük: buradaki en güçlü sektörle en zayıfı arasındaki fark yaklaşık dörtte üç puan. 3,35’teki bir sektörle 2,60’taki bir sektör, aynı şirketin farklı bütçeli iki versiyonu değil — veri konusunda, bir müşteri probleminin sahibinin kim olduğu konusunda ve bir pilotun bitmesine izin verilip verilmediği konusunda farklı kararlar vermiş organizasyonlar.',
        'DAIMI’den önce DMI ile AIMI’yi ayrı okuyun. Bir sektör saygın bir digital skoru tutarken AI skoru bir tam puan altında kalabiliyor; çoğu şirketin dürüst tablosu da bu: temeller büyük ölçüde atılmış, üzerine gelen capability daha yeni başlamış. Bankacılık ikisinin birbirine yaklaştığı tek yer. Turizm ise tersinin en net örneği.',
      ],
    },
  ],
}
